/**
 * Plantilla de Excel de Generar Fichas de Pago.
 *
 * Sin React ni antd a propósito: el workbook se puede generar y revisar con un
 * `node script.js`, sin levantar la app.
 *
 * La hoja de datos va PRIMERO porque el importador siempre lee la primera hoja del archivo
 * que se sube: si las instrucciones estuvieran antes, la plantilla llenada a mano se subiría
 * vacía.
 */

import * as XLSXStyle from "xlsx-js-style";
import { border, headerStyle } from "../../../../../Components/ExcelEstilos";
import { COLUMNAS_ESPERADAS, MODOS, nombreAlumno } from "./analizarFichasPago";

export const NOMBRE_PLANTILLA = "plantillaFichasDePago.xlsx";

const COLOR_TITULO = "007A8A";
const COLOR_SUAVE = "D6F4F8";

// ─── Estilos ─────────────────────────────────────────────────────────────────

const tituloStyle = {
  font: { bold: true, sz: 13, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: COLOR_TITULO } },
  alignment: { horizontal: "left", vertical: "center" },
};

const subtituloStyle = {
  font: { bold: true, sz: 11, color: { rgb: "0B4F5A" } },
  fill: { fgColor: { rgb: COLOR_SUAVE } },
  alignment: { vertical: "center", wrapText: true },
};

const notaStyle = {
  font: { sz: 10, color: { rgb: "434343" } },
  alignment: { vertical: "center", wrapText: true },
};

const avisoStyle = {
  font: { bold: true, sz: 10, color: { rgb: "A8071A" } },
  alignment: { vertical: "center", wrapText: true },
};

const celdaStyle = { border, alignment: { vertical: "center", wrapText: true } };

const celdaCentradaStyle = { border, alignment: { vertical: "center", horizontal: "center" } };

// La fila de ejemplo va en gris cursivo para que se lea como lo que es: algo que hay que
// borrar antes de subir el archivo.
const ejemploStyle = {
  font: { italic: true, sz: 10, color: { rgb: "8C8C8C" } },
  border,
  alignment: { vertical: "center", wrapText: true },
};

const faltaStyle = {
  font: { bold: true, sz: 10, color: { rgb: "A8071A" } },
  border,
  alignment: { vertical: "center", horizontal: "center" },
};

const okStyle = {
  font: { sz: 10, color: { rgb: "237804" } },
  border,
  alignment: { vertical: "center", horizontal: "center" },
};

// ─── Celdas ──────────────────────────────────────────────────────────────────

// `z: "@"` deja la celda con formato de texto: así Excel no convierte "NOV-2026" ni
// "15/11/2026" en fechas, ni se come los ceros de una matrícula, cuando se escribe encima.
const celda = (valor, estilo = celdaStyle) => ({
  v: valor === undefined || valor === null ? "" : String(valor),
  t: "s",
  s: estilo,
  z: "@",
});

const hojaConEncabezado = (encabezados, filas, anchos) => {
  const hoja = XLSXStyle.utils.aoa_to_sheet([encabezados.map((titulo) => celda(titulo, headerStyle)), ...filas]);
  hoja["!cols"] = anchos.map((wch) => ({ wch }));
  hoja["!rows"] = [{ hpt: 26 }];
  return hoja;
};

// ─── Hojas ───────────────────────────────────────────────────────────────────

/**
 * Hoja 1: el formato que se sube. Dos filas de ejemplo del mismo alumno para que se vea que
 * cada concepto va en su renglón (y sale en su propia ficha, con su propia referencia).
 */
const hojaFichas = () => {
  const ejemplo = (valores) =>
    COLUMNAS_ESPERADAS.map((columna) =>
      celda(valores[columna.clave] !== undefined ? valores[columna.clave] : columna.ejemplo, ejemploStyle),
    );

  return hojaConEncabezado(
    COLUMNAS_ESPERADAS.map((columna) => columna.etiqueta),
    [
      ejemplo({ concepto: "INSCRIPCION", monto: "1800.00", periodo: "2026-2027" }),
      ejemplo({ concepto: "COLEGIATURA", monto: "2500.00", periodo: "NOV-2026" }),
    ],
    COLUMNAS_ESPERADAS.map((columna) => columna.ancho),
  );
};

const LINEAS_INSTRUCCIONES = [
  [tituloStyle, "GENERAR FICHAS DE PAGO A ALUMNOS"],
  [notaStyle, "Panel → Escuela → pestaña Alumnos → botón Generar Fichas de Pago."],
  [notaStyle, ""],
  [subtituloStyle, "Qué se cobra (se elige en la pantalla antes de subir el archivo)"],
  [
    notaStyle,
    "LO QUE DIGA EL ARCHIVO: cada renglón (matrícula, concepto y monto) es una ficha. Sirve para colegiaturas, inscripciones o cualquier cargo.",
  ],
  [
    notaStyle,
    "LA LISTA ESCOLAR DEL ALUMNO: basta la matrícula. Cada alumno recibe UNA ficha con lo que todavía no tiene de su lista de grupo y de su lista individual (lo mismo que «Compra Directa» de la tienda), al precio de su lista de precios. Lo que ya tiene en una venta, pagada o con ficha pendiente, no se vuelve a cobrar. Las columnas CONCEPTO, MONTO y PERIODO se ignoran.",
  ],
  [
    notaStyle,
    "En las fichas de lista, los libros virtuales reciben su código cuando se confirma el pago, y los artículos físicos quedan pendientes de entregar (no se aparta inventario).",
  ],
  [notaStyle, ""],
  [subtituloStyle, "Qué hace con cada renglón"],
  [
    notaStyle,
    "Le registra al alumno una remisión PENDIENTE DE PAGO por el concepto y el monto del renglón, con una referencia de pago única y las cuentas bancarias de su escuela (las de la transferencia fija de Configuración eCommerce).",
  ],
  [
    notaStyle,
    "El alumno paga por transferencia usando la referencia. El pago se aplica con lo que ya existe: Ventas eCommerce → Conciliar transferencias por Excel (con el estado de cuenta del banco), Confirmar transferencia, Pagar manualmente, o desde Cobranza.",
  ],
  [
    notaStyle,
    "Cada renglón es su propia ficha con su propia referencia: si un alumno debe inscripción y colegiatura, van en dos renglones y puede pagarlas por separado.",
  ],
  [notaStyle, ""],
  [subtituloStyle, "Al generar eliges"],
  [notaStyle, "Enviar por correo: le manda la ficha al correo del alumno y a sus correos adicionales."],
  [
    notaStyle,
    "Mostrar en la tienda en línea: la ficha aparece en «Mis pedidos» de la cuenta del alumno, desde donde la puede imprimir y reenviar. Sin esta opción no sale en la tienda ni en Ventas eCommerce, pero se cobra igual con la conciliación por Excel o desde Cobranza.",
  ],
  [notaStyle, "Descargar PDF: un PDF con todas las fichas generadas, una por hoja, para imprimir o repartir."],
  [notaStyle, ""],
  [subtituloStyle, "Lo que NO se genera (y te lo reporta)"],
  [avisoStyle, "Si la matrícula o el concepto no existen, o le quedan a más de un registro."],
  [
    avisoStyle,
    "Si la escuela del alumno no tiene configurada la transferencia fija con al menos una cuenta bancaria (hoja «Escuelas»).",
  ],
  [
    avisoStyle,
    "Si al concepto le falta configuración: código del SAT, familia o cuenta de ingreso a crédito (hoja «Conceptos»).",
  ],
  [
    avisoStyle,
    "Si el alumno ya tiene una ficha activa del mismo concepto y periodo. Sin periodo, sólo si la anterior sigue sin pagar. Por eso puedes volver a subir el mismo archivo sin duplicar fichas.",
  ],
  [notaStyle, ""],
  [subtituloStyle, "Cómo se llena"],
  [
    notaStyle,
    "MONTO es el total a pagar CON impuestos (lo que el alumno transfiere). Si lo dejas vacío se usa el precio de lista del alumno más impuestos. Por el redondeo de impuestos el total puede quedar un centavo arriba o abajo; la ficha muestra el total final.",
  ],
  [notaStyle, "FECHA LIMITE sólo se imprime: la ficha no se cancela sola al pasar la fecha."],
  [
    notaStyle,
    'Las columnas se reconocen por su encabezado, no por su posición (ver la hoja "Columnas"). Sólo MATRICULA y CONCEPTO son obligatorias.',
  ],
  [avisoStyle, "Borra las filas de ejemplo antes de subir el archivo: esa matrícula no existe y se reporta como error."],
  [
    notaStyle,
    'El importador siempre lee la PRIMERA hoja del archivo. No cambies el orden de las hojas ni agregues otra antes de "Fichas".',
  ],
  [notaStyle, ""],
  [
    notaStyle,
    "La generación corre en el servidor y puede tardar varios minutos. Puedes salir de la pantalla: sigue sola y al volver ves su avance. Con Detener se para limpio; al volver a subir el archivo, lo que ya quedó sale como «Ya tiene ficha».",
  ],
];

const hojaInstrucciones = () => {
  const hoja = XLSXStyle.utils.aoa_to_sheet(LINEAS_INSTRUCCIONES.map(([estilo, texto]) => [celda(texto, estilo)]));
  hoja["!cols"] = [{ wch: 118 }];
  hoja["!rows"] = LINEAS_INSTRUCCIONES.map(([estilo]) => (estilo === tituloStyle ? { hpt: 26 } : { hpt: 16 }));
  return hoja;
};

// "Sí" si se pide en los dos modos; si sólo en uno, cuál.
const textoObligatoria = (clave) => {
  const enArchivo = MODOS.concepto.obligatorias.includes(clave);
  const enLista = MODOS.lista.obligatorias.includes(clave);
  if (enArchivo && enLista) return "Sí";
  if (enArchivo) return "Sólo con «Lo que diga el archivo»";
  if (enLista) return "Sólo con «La lista escolar»";
  return "No";
};

const hojaColumnas = () =>
  hojaConEncabezado(
    ["Columna", "Obligatoria", "Se reconoce si el encabezado empieza con", "Para qué sirve"],
    COLUMNAS_ESPERADAS.map((columna) => [
      celda(columna.etiqueta),
      celda(textoObligatoria(columna.clave), textoObligatoria(columna.clave) === "No" ? celdaCentradaStyle : faltaStyle),
      celda(columna.prefijo),
      celda(columna.ayuda),
    ]),
    [18, 22, 34, 90],
  );

const vacia = (columnas, mensaje) => [[celda(mensaje), ...Array.from({ length: columnas - 1 }, () => celda(""))]];

/**
 * Catálogo de consulta: los insumos de venta que se pueden cobrar y si están listos. Los
 * servicios van primero porque son los conceptos típicos de una ficha (colegiatura,
 * inscripción).
 */
const hojaConceptos = (catalogos) => {
  const conceptos = [...(catalogos?.conceptos || [])].sort(
    (a, b) => Number(Boolean(b.servicio)) - Number(Boolean(a.servicio)) || String(a.descripcion).localeCompare(String(b.descripcion)),
  );

  const filas =
    conceptos.length > 0
      ? conceptos.map((concepto) => [
          celda(concepto.clave),
          celda(concepto.codigo),
          celda(concepto.descripcion),
          concepto.servicio ? celda("Sí", celdaCentradaStyle) : celda("No", celdaCentradaStyle),
          concepto.problema ? celda("FALTA", faltaStyle) : celda("Sí", okStyle),
          celda(concepto.problema),
        ])
      : vacia(6, "(no hay insumos marcados de venta)");

  return hojaConEncabezado(
    ["Clave", "Código", "Descripción", "¿Servicio?", "¿Listo para fichas?", "Qué le falta"],
    filas,
    [18, 10, 46, 11, 18, 60],
  );
};

const hojaEscuelas = (catalogos) => {
  const escuelas = catalogos?.escuelas || [];
  const filas =
    escuelas.length > 0
      ? escuelas.map((escuela) => [
          celda(escuela.nombre),
          celda(escuela.clave),
          celda(escuela.bancos, celdaCentradaStyle),
          escuela.problema ? celda("FALTA", faltaStyle) : celda("Sí", okStyle),
          celda(escuela.problema),
        ])
      : vacia(5, "(no hay escuelas)");

  return hojaConEncabezado(
    ["Escuela", "Clave", "Cuentas bancarias", "¿Lista para fichas?", "Qué le falta"],
    filas,
    [36, 12, 18, 18, 70],
  );
};

const hojaAlumnos = (catalogos) => {
  const escuelasPorId = new Map((catalogos?.escuelas || []).map((escuela) => [String(escuela._id), escuela.nombre]));
  const gruposPorId = new Map((catalogos?.grupos || []).map((grupo) => [String(grupo._id), grupo.nombre]));
  const alumnos = [...(catalogos?.alumnos || [])].sort((a, b) =>
    String(a.matricula).localeCompare(String(b.matricula), "es", { numeric: true }),
  );

  const filas =
    alumnos.length > 0
      ? alumnos.map((alumno) => [
          celda(alumno.matricula),
          celda(nombreAlumno(alumno)),
          celda(escuelasPorId.get(String(alumno.escuelaId)) || ""),
          celda(gruposPorId.get(String(alumno.grupoEscuelaId)) || ""),
          celda([alumno.correo, ...(alumno.correosAdicionales || [])].filter(Boolean).join(", ")),
        ])
      : vacia(5, "(no hay alumnos)");

  return hojaConEncabezado(["Matrícula", "Alumno", "Escuela", "Grupo", "Correos"], filas, [18, 40, 30, 24, 46]);
};

// ─── Workbook ────────────────────────────────────────────────────────────────

export const construirPlantilla = (catalogos) => {
  const libro = XLSXStyle.utils.book_new();

  XLSXStyle.utils.book_append_sheet(libro, hojaFichas(), "Fichas");
  XLSXStyle.utils.book_append_sheet(libro, hojaInstrucciones(), "Cómo funciona");
  XLSXStyle.utils.book_append_sheet(libro, hojaColumnas(), "Columnas");
  XLSXStyle.utils.book_append_sheet(libro, hojaConceptos(catalogos), "Conceptos");
  XLSXStyle.utils.book_append_sheet(libro, hojaEscuelas(catalogos), "Escuelas");
  XLSXStyle.utils.book_append_sheet(libro, hojaAlumnos(catalogos), "Alumnos");

  return libro;
};
